import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Heart, Eye, Crown, Sword, ChevronLeft, Search, Sparkles, Trophy,
  ArrowUpDown, BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { getStoredLang } from "@/utils/i18n";
import ambientMusic from "@/utils/AmbientMusic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORTS = {
  recent: { label_it: "Più Recenti", label_en: "Most Recent" },
  likes: { label_it: "Più Apprezzate", label_en: "Most Liked" },
  chapters: { label_it: "Più Capitoli", label_en: "Longest Sagas" },
  bosses: { label_it: "Più Boss", label_en: "Most Bosses" },
};

const LANG_FILTERS = [
  { id: "", label_it: "Tutte le lingue", label_en: "All languages" },
  { id: "it", label_it: "Italiano", label_en: "Italian" },
  { id: "en", label_it: "Inglese", label_en: "English" },
  { id: "es", label_it: "Spagnolo", label_en: "Spanish" },
  { id: "fr", label_it: "Francese", label_en: "French" },
  { id: "de", label_it: "Tedesco", label_en: "German" },
];

const PAGE_SIZE = 20;

/**
 * Hall of Stories — public showcase. Browseable without login.
 * Logged-in users get a heart button for likes (toggled instantly).
 */
export default function Hall() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const lang = getStoredLang();
  const isIt = lang === "it";

  const [stories, setStories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [sort, setSort] = useState("recent");
  const [language, setLanguage] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort, skip: String(skip), limit: String(PAGE_SIZE) });
      if (language) params.set("language", language);
      if (q) params.set("q", q);
      const res = await axios.get(`${API}/hall?${params.toString()}`, { headers });
      setStories(res.data?.stories || []);
      setTotal(res.data?.total || 0);
    } catch {
      setStories([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, skip, language, q]);

  useEffect(() => { load(); }, [load]);
  // iter 74 — dedicated music for the public Hall
  useEffect(() => { ambientMusic.play('hall'); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    setQ(qInput.trim());
  };

  const toggleLike = async (story, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!token) {
      navigate("/auth");
      return;
    }
    try {
      const next = !story.liked_by_me;
      // optimistic
      setStories((cur) =>
        cur.map((s) =>
          s.id === story.id
            ? { ...s, liked_by_me: next, likes_count: Math.max(0, s.likes_count + (next ? 1 : -1)) }
            : s
        )
      );
      if (next) {
        await axios.post(`${API}/hall/${story.id}/like`, {}, { headers });
      } else {
        await axios.delete(`${API}/hall/${story.id}/like`, { headers });
      }
    } catch {
      // rollback on failure
      load();
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="min-h-screen bg-[#0a0a0c] relative" data-testid="hall-page">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c] via-[#1a1a1d] to-[#0a0a0c] pointer-events-none" />
      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-96 h-96 bg-[#cfb53b] rounded-full opacity-[0.04] blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-3 sm:px-4 py-6 sm:py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sm:mb-8 gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(token ? "/" : "/auth")}
              className="text-[#94a3b8] hover:text-[#cfb53b]"
              data-testid="hall-back-btn"
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline ml-1">{isIt ? "Indietro" : "Back"}</span>
            </Button>
            <div className="min-w-0">
              <h1 className="font-heading text-2xl sm:text-4xl font-bold text-[#cfb53b] flex items-center gap-2">
                <Trophy size={26} className="text-[#cfb53b]" />
                Hall of Stories
              </h1>
              <p className="text-xs sm:text-sm text-[#94a3b8] mt-1">
                {isIt
                  ? "Le saghe più epiche, condivise dai giocatori"
                  : "The most epic sagas, shared by the players"}
              </p>
            </div>
          </div>
          {!user && (
            <Button
              onClick={() => navigate("/auth")}
              className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading shrink-0"
              data-testid="hall-login-cta"
            >
              {isIt ? "Accedi" : "Sign In"}
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-6">
          <form onSubmit={onSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b]" />
              <Input
                data-testid="hall-search"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder={isIt ? "Cerca per titolo o personaggio..." : "Search by title or character..."}
                className="bg-[#0a0a0c] border-[#cfb53b]/20 text-[#e2e8f0] pl-9 h-10"
              />
            </div>
            <Button type="submit" variant="outline" className="border-[#cfb53b]/30 text-[#cfb53b] hover:bg-[#cfb53b]/10 h-10 shrink-0">
              {isIt ? "Cerca" : "Search"}
            </Button>
          </form>
          <Select value={sort} onValueChange={(v) => { setSort(v); setSkip(0); }}>
            <SelectTrigger className="w-full sm:w-44 bg-[#0a0a0c] border-[#cfb53b]/20 text-[#e2e8f0] h-10" data-testid="hall-sort">
              <ArrowUpDown size={14} className="mr-1 text-[#cfb53b]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1d] border-[#cfb53b]/30">
              {Object.entries(SORTS).map(([id, s]) => (
                <SelectItem key={id} value={id} className="text-[#e2e8f0]">
                  {isIt ? s.label_it : s.label_en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={language || "_all"} onValueChange={(v) => { setLanguage(v === "_all" ? "" : v); setSkip(0); }}>
            <SelectTrigger className="w-full sm:w-44 bg-[#0a0a0c] border-[#cfb53b]/20 text-[#e2e8f0] h-10" data-testid="hall-lang-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1d] border-[#cfb53b]/30">
              {LANG_FILTERS.map((l) => (
                <SelectItem key={l.id || "_all"} value={l.id || "_all"} className="text-[#e2e8f0]">
                  {isIt ? l.label_it : l.label_en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#cfb53b] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : stories.length === 0 ? (
          <div className="text-center py-16 sm:py-24 border border-dashed border-[#cfb53b]/20 rounded-lg">
            <BookOpen size={40} className="mx-auto text-[#cfb53b]/30 mb-3" />
            <p className="text-[#94a3b8] font-body">
              {isIt ? "Nessuna saga corrisponde ai filtri." : "No sagas match these filters."}
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" data-testid="hall-grid">
            {stories.map((s) => (
              <SagaCard key={s.id} story={s} onLike={(ev) => toggleLike(s, ev)} isIt={isIt} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && total > PAGE_SIZE && (
          <div className="flex justify-center items-center gap-3 mt-8" data-testid="hall-pagination">
            <Button
              size="sm"
              variant="outline"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              className="border-[#cfb53b]/30 text-[#cfb53b] hover:bg-[#cfb53b]/10"
            >
              {isIt ? "Precedente" : "Prev"}
            </Button>
            <span className="text-xs sm:text-sm text-[#94a3b8] font-body">
              {currentPage} / {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={skip + PAGE_SIZE >= total}
              onClick={() => setSkip(skip + PAGE_SIZE)}
              className="border-[#cfb53b]/30 text-[#cfb53b] hover:bg-[#cfb53b]/10"
            >
              {isIt ? "Successivo" : "Next"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function SagaCard({ story, onLike, isIt }) {
  const cover = story.cover_image_url;
  const lastBoss = story.last_boss;
  const char = story.character || {};

  return (
    <Link
      to={`/hall/${story.id}`}
      className="group block bg-[#1a1a1d]/80 border border-[#cfb53b]/15 rounded-lg overflow-hidden transition-all hover:border-[#cfb53b]/50 hover:bg-[#1a1a1d] hover:-translate-y-0.5"
      data-testid={`hall-card-${story.id}`}
    >
      {/* Cover */}
      <div className="relative h-40 sm:h-48 bg-[#0a0a0c] overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt={story.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#cfb53b]/20">
            <BookOpen size={48} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-transparent to-transparent" />
        {/* Last boss inset */}
        {lastBoss?.image_url && (
          <div className="absolute top-2 right-2 w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden border-2 border-[#8b0000]/70 shadow-lg">
            <img src={lastBoss.image_url} alt={lastBoss.name} className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#8b0000]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[8px] font-heading text-white bg-black/70 truncate">
              {lastBoss.name}
            </div>
          </div>
        )}
        {/* Like button overlay */}
        <button
          type="button"
          onClick={onLike}
          className={`absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-heading transition-all ${
            story.liked_by_me
              ? "bg-pink-600/90 text-white"
              : "bg-black/70 text-white hover:bg-pink-700/80"
          }`}
          data-testid={`hall-like-${story.id}`}
        >
          <Heart size={12} fill={story.liked_by_me ? "currentColor" : "none"} />
          <span>{story.likes_count}</span>
        </button>
      </div>

      {/* Body */}
      <div className="p-3 sm:p-4 space-y-2">
        <h3 className="font-heading text-base sm:text-lg text-[#e2e8f0] group-hover:text-[#cfb53b] transition-colors line-clamp-2 leading-tight">
          {story.title}
        </h3>
        <div className="flex items-center gap-1.5 text-xs text-[#94a3b8] flex-wrap">
          <span className="font-heading text-[#cfb53b]/80">{char.name || "?"}</span>
          <span className="text-[#cfb53b]/30">·</span>
          <span className="capitalize">{char.class_type}</span>
          {char.level ? <span className="text-[#cfb53b]/30">·</span> : null}
          {char.level ? <span>L{char.level}</span> : null}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-[#94a3b8] pt-1">
          <span className="flex items-center gap-1" title={isIt ? "Capitoli" : "Chapters"}>
            <Sparkles size={11} className="text-[#cfb53b]" />
            {story.chapters}
          </span>
          <span className="flex items-center gap-1" title={isIt ? "Boss" : "Bosses"}>
            <Crown size={11} className="text-[#8b0000]" />
            {story.bosses_defeated}
          </span>
          <span className="flex items-center gap-1" title={isIt ? "Pagine" : "Pages"}>
            <Sword size={11} className="text-[#94a3b8]" />
            {story.total_pages}
          </span>
          <span className="flex items-center gap-1 ml-auto" title={isIt ? "Visite" : "Views"}>
            <Eye size={11} />
            {story.views}
          </span>
        </div>
        <p className="text-[#94a3b8] text-xs line-clamp-2 italic min-h-[28px]">
          {story.description || (isIt ? "Una saga senza parole..." : "A wordless saga...")}
        </p>
        <div className="flex items-center justify-between pt-1 border-t border-[#cfb53b]/10 text-[10px] text-[#64748b]">
          <span className="truncate">{isIt ? "di" : "by"} <span className="text-[#cfb53b]/80">{story.author_username}</span></span>
          {story.language && (
            <span className="uppercase tracking-wider">{story.language}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
